import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  Checkbox,
  cn,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import RadioInput from '@/enterprise/components/RadioInput';
import { useReporting } from '@/reporting';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useLendingModalContext } from './LendingModalContext';
import { CapitalAmountOptions, PrioritiesOptions, ProductsOptions } from './types';
import useLendingFormContext from './useLendingFormContext';

const GoalsStep = () => {
  const { trackEnterpriseEvent } = useReporting();
  const { referrer } = useLendingModalContext();
  const {
    register,
    control,
    formState: {
      errors: { goals: goalsErrors },
    },
    clearErrors,
  } = useLendingFormContext();

  return (
    <div className="mt-4 flex flex-col space-y-4 rounded bg-gray-50 p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2">
        {/* How much capital are you seeking? */}
        <div>
          <Label htmlFor="capital" required>
            How much capital are you seeking?
          </Label>
          <Controller
            control={control}
            name="goals.capitalAmount"
            render={({ field }) => (
              <div>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    clearErrors('goals.capitalAmount');
                  }}
                  value={field.value ?? ''}
                >
                  <SelectTrigger aria-label="How much capital are you seeking?" hasError={!!goalsErrors?.capitalAmount}>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {CapitalAmountOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {goalsErrors?.capitalAmount && (
                  <p className="text-sm text-red-500">{goalsErrors.capitalAmount.message}</p>
                )}
              </div>
            )}
            rules={{ required: 'Required' }}
          />
        </div>

        {/* Do you have existing financing? */}
        <div>
          <Label htmlFor="existingFinancing" required>
            Do you have existing financing?
          </Label>
          <Controller
            control={control}
            name="goals.existingFinancing"
            render={({ field }) => (
              <div className="flex gap-2">
                <RadioInput
                  label="Yes"
                  value="Yes"
                  onChange={() => {
                    field.onChange(true);
                    clearErrors('goals.existingFinancing');
                  }}
                  checked={field.value === true}
                  className={cn('bg-white p-3 font-normal', { 'border-red-500': !!goalsErrors?.existingFinancing })}
                />
                <RadioInput
                  label="No"
                  value="No"
                  onChange={() => {
                    field.onChange(false);
                    clearErrors('goals.existingFinancing');
                  }}
                  checked={field.value === false}
                  className={cn('bg-white p-3 font-normal', { 'border-red-500': !!goalsErrors?.existingFinancing })}
                />
              </div>
            )}
            rules={{
              validate: (value) => {
                if (value === undefined) {
                  return 'Required';
                }

                return true;
              },
            }}
          />
          {goalsErrors?.existingFinancing && (
            <p className="text-sm text-red-500">{goalsErrors.existingFinancing.message}</p>
          )}
        </div>
      </div>

      {/* What products are you looking for? */}
      <div>
        <Label htmlFor="products" required>
          What products are you looking for?
        </Label>
        <Controller
          control={control}
          name="goals.products"
          render={({ field }) => (
            <div
              className={cn('grid grid-cols-1 gap-2 p-2 sm:grid-cols-2', {
                'rounded border border-red-500': !!goalsErrors?.products,
              })}
            >
              {ProductsOptions.map((option) => {
                const isSelected = field.value.includes(option);

                return (
                  <button
                    key={option}
                    aria-label={option}
                    className={cn(
                      'flex items-center gap-4 rounded border border-transparent bg-white p-2 shadow transition-colors duration-200 hover:border-primary-300 hover:bg-primary-50',
                      {
                        'border-primary-500 bg-primary-100': isSelected,
                      }
                    )}
                    onClick={() => {
                      field.onChange(
                        field.value.includes(option)
                          ? field.value.filter((value) => value !== option)
                          : [...(field.value ?? []), option]
                      );
                      clearErrors('goals.products');
                    }}
                    type="button"
                  >
                    <Checkbox color="primary" checked={isSelected} readOnly />
                    <div>{option}</div>
                  </button>
                );
              })}
            </div>
          )}
          rules={{ required: 'Required' }}
        />
        {goalsErrors?.products && <p className="text-sm text-red-500">{goalsErrors.products.message}</p>}
        <div className="flex items-center gap-2">
          <p className="text-text-secondary">Not sure what to select?</p>
          <Link
            className="font-semibold text-blue-500"
            to={asEnterpriseLink('lending')}
            target="_blank"
            onClick={() => {
              trackEnterpriseEvent('lending::modal::learn-more-products::clicked', { referrer });
            }}
          >
            Learn more about our financing options
          </Link>
        </div>
      </div>

      {/* What are your financial priorities? */}
      <div>
        <Label htmlFor="financialPriorities" required>
          What are your financial priorities?
        </Label>
        <Controller
          control={control}
          name="goals.priorities"
          render={({ field }) => (
            <div
              className={cn('grid grid-cols-1 gap-2 p-2 sm:grid-cols-2', {
                'rounded border border-red-500': !!goalsErrors?.priorities,
              })}
            >
              {PrioritiesOptions.map((option) => {
                const isSelected = field.value.includes(option);

                return (
                  <button
                    key={option}
                    aria-label={option}
                    className={cn(
                      'flex items-center gap-4 rounded border border-transparent bg-white p-2 shadow transition-colors duration-200 hover:border-primary-300 hover:bg-primary-50',
                      {
                        'border-primary-500 bg-primary-100': isSelected,
                      }
                    )}
                    onClick={() => {
                      field.onChange(
                        field.value.includes(option)
                          ? field.value.filter((value) => value !== option)
                          : [...(field.value ?? []), option]
                      );
                      clearErrors('goals.priorities');
                    }}
                    type="button"
                  >
                    <Checkbox color="primary" checked={isSelected} readOnly />
                    <div>{option}</div>
                  </button>
                );
              })}
            </div>
          )}
          rules={{ required: 'Required' }}
        />
        {goalsErrors?.priorities && <p className="text-sm text-red-500">{goalsErrors.priorities.message}</p>}
      </div>

      {/* What does a successful funding experience mean to you? */}
      <div>
        <Label htmlFor="successfulFundingExperience">What does a successful funding experience mean to you?</Label>
        <TextInput
          id="successfulFundingExperience"
          className="w-full"
          hasError={!!goalsErrors?.successfulFundingExperience}
          {...register('goals.successfulFundingExperience', {
            maxLength: 255,
          })}
        />
        {goalsErrors?.successfulFundingExperience?.type === 'maxLength' && (
          <p className="text-sm text-red-500">Maximum length is 255 characters</p>
        )}
      </div>

      {/* What's your favorite go-to meal? */}
      <div>
        <Label htmlFor="favoriteMeal">What&apos;s your favorite go-to meal?</Label>
        <TextInput id="favoriteMeal" className="w-full" {...register('goals.favoriteMeal')} />
      </div>
    </div>
  );
};

export default GoalsStep;
